import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout1 from "../../DataLayouts/Layout1";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import {MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_leave_reason_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "leave_reason_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

function LeaveReasonList() {
  const [leaveReason, setLeaveReason] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_leave_reason_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/leave-reasons-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const b = data.map((item) => {
          const reasonId =
            item.organization_leave_reason_id ?? item.id ?? null;
          return {
            ...item,
            organization_leave_reason_id: reasonId,
            id: reasonId,
            leave_reason_type_name:
              item?.leave_reason_type_name || item?.leavereasontype || "",
            leavereasontype:
              item?.leave_reason_type_name || item?.leavereasontype || "",
            description: item?.description == null ? "" : item?.description,
          };
        });

        setLeaveReason(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching leave reasons:", error);
        toast.error("Failed to load leave reasons");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      const reasonId = id ?? null;
      if (
        reasonId === null ||
        reasonId === undefined ||
        reasonId === "" ||
        reasonId === "undefined" ||
        Number.isNaN(Number(reasonId))
      ) {
        toast.error("Unable to delete Leave Reason: missing id.");
        const err = new Error("Invalid leave reason id");
        err.response = { status: 422 };
        throw err;
      }

      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/leave-reason/${reasonId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Leave Reason deleted successfully");
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
          return;
        }

        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
          const err = new Error("Unauthorized");
          err.response = { status: 401 };
          throw err;
        }

        const errorMessage =
          errorData.message ||
          errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
          "Failed to delete Leave Reason";
        toast.error(errorMessage);
        const err = new Error(errorMessage);
        err.response = { status: response.status, data: errorData };
        throw err;
      } catch (error) {
        if (!error?.response) {
          console.error("Delete failed:", error);
          toast.error("Failed to delete Leave Reason");
          error.response = { status: 500 };
        }
        throw error;
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );


const handleEdit = useCallback(
    (item) => {
      const reasonId =
        item?.organization_leave_reason_id ?? item?.id;
      if (!reasonId) {
        toast.error("Unable to open Leave Reason: missing id.");
        return;
      }
      navigate(`/organization-configration/leave-reason/edit/${reasonId}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      const reasonId =
        item?.organization_leave_reason_id ?? item?.id;
      if (!reasonId) {
        toast.error("Unable to open Leave Reason: missing id.");
        return;
      }
      navigate(`/organization-configration/leave-reason/view/${reasonId}`);
    },
    [navigate]
  );




  return (
    <>
    <Layout4
      loading={loading}
      heading={"Leave Reasons"}
      add_action={"LEAVE_REASON_ADD"}
      btnName={"Add Leave Reason"}
      Data={leaveReason}
       delete_action={"LEAVE_REASON_DELETE"}
      tableHeaders={[
        {name : "Leave Reasons" , value_key : "leave_reason_name" ,textStyle: "capitalize",},
          {name : "Leave Reason Type" , value_key : "reason_type" ,textStyle: "capitalize",},
           {name : "Description" , value_key : "description" },
        
      ]}
      Icons={[
        <FormatAlignJustifyIcon
          sx={{ fontSize: 60, color: "grey.500", mb: 2 }}
        />,
        <FormatAlignJustifyIcon color="primary" />,
        <NextWeekIcon sx={{ color: "text.secondary" }} />,
      ]}
      messages={[
        "leave reasons",
        "leave reasons",
        "Add Leave Reasons",
        "leave reasons",
      ]}
      Route={"/organization-configration/leave-reason"}
      setData={setLeaveReason}
      DeleteFunc={handleDelete}
    />




      <TableDataGeneric
        tableName="organization_leave_reasons"
        primaryKey="organization_leave_reason_id"
        heading="Leave Reasons"
        data={leaveReason}
        sortname="leave_reason_name"
        showActions={true}
        Route="/organization-configration/leave-reason"
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        handleShow={handleShow}
        edit_delete_action={["LEAVE_REASON_DELETE", "LEAVE_REASON_EDIT"]}
        token={localStorage.getItem("token")}
        organizationUserId={userData?.organization_user_id}
        showLayoutButtons={true}
        paginationData={paginationData}
        filterModule="organization_leave_reasons"
        config={{
          defaultVisibleColumns: ["leave_reason_name","leavereasontype","description"],
          mandatoryColumns: ["leave_reason_name","leavereasontype","description"],
        }}
      />
 
    </>
  );
}

export default LeaveReasonList;
